import React, { useEffect, useMemo, useRef, useState } from "react";
import SubmitTicketModal from "./SubmitTicketModal";
import ManagerReviewModal from "./ManagerReviewModal";
import Form from "../../Form";
import TicketProcessingDetails from "./TicketProcessingDetails";
import styles from "./DataInput.module.scss";
import TicketProcessingInput from "./TicketProcessingInput";
import PDFOption from "./PDFOption";
import {
  buildTicketUrl,
  DataInputConstants,
  dummyArray,
  sideBarConstants,
  submitValidationSchema,
  successNotificationStyle,
} from "./constants";
import ConditionalRender from "../../../reusable-components/ConditionalRender";
import FileViewSection from "./FileViewSection";
import ReplaceTicketModal from "./ReplaceTicketModal";
import TicketProcessingServices from "../../../services/ticketProcessing.service";
import { useTicketProcessingFilterContext } from "../../../context/TicketProcessingFilterContext";
import { useLocation } from "react-router-dom";
import moment from "moment";
import {
  TicketProcessingReviewType,
  TicketSubmitAction,
} from "../../../enums/TicketProcessingReview.enum";
import SuccessIcon from "../../../assets/images/accept-icon.svg";
import Loading from "../../../Common/Loading";
import { TabDataTypes } from "../../../enums/ViewListingTypes";
import { useQueryClient } from "@tanstack/react-query";
import { DateFormat } from "../../../enums/dateTimeFormat";
import { fieldConstants } from "./TicketProcessingInput/constants";
import { message, notification, Skeleton } from "antd";
import TNLoader from "../../../reusable-components/TNLoader";
import { storageKeys } from "../../../constants/storageKeys";
import TicketSearchInput from "./SearchField";
import { QueryKeys } from "../../../constants/QueryKeys";

interface DataInputProps {
  isManagerReview: boolean;
}

type FormFieldsType = {
  resetFormFields: () => void;
};

const DataInput = (props: DataInputProps) => {
  const { isManagerReview } = props;
  const skipRefreshRef = useRef<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const fieldsSectionRef = useRef<FormFieldsType>(null);
  const location = useLocation();
  const queryClient = useQueryClient();
  const userDetails = localStorage.getItem(DataInputConstants.USER_DETAILS_KEY);
  const userDetailsJson = JSON.parse(userDetails);
  const userId = userDetailsJson[DataInputConstants.ID_VALUE_KEY];

  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [restoredFromSession, setRestoredFromSession] = useState(false);
  const [isReplaceTktModalOpen, setIsReplaceTktModalOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState(undefined);

  const { processingFilters } = useTicketProcessingFilterContext();

  const dataInputSuccess = useMemo(
    () => ({
      message: DataInputConstants.SUCCESS_HEADER,
      icon: (
        <img className={styles.successNotificationIcon} src={SuccessIcon} />
      ),
      className: styles.successNotification,
      style: successNotificationStyle,
    }),
    [],
  );

  const {
    FetchTicketProcessingReviewList,
    FetchTicketProcessingReviewDetails,
    SubmitTicketProcessingReview,
  } = TicketProcessingServices();

  const { data, refetch, isPending, isFetching } =
    FetchTicketProcessingReviewList({
      search: searchInputValue,
      companyId: processingFilters.customerName,
      jobId: processingFilters.jobName,
      truckId: processingFilters.truckNumber,
      startDate: location?.state?.startDate,
      endDate: location?.state?.endDate,
      status: !isManagerReview
        ? TicketProcessingReviewType.INPUT
        : TicketProcessingReviewType.MANAGER,
      userId: userId,
    });

  const [ticketUploadUrl, setTicketUploadUrl] = useState({});
  const [ticketData, setTicketData] = useState({});
  const [activeTicket, setActiveTicket] = useState({});

  const {
    data: ticketDetails,
    refetch: refetchDetails,
    isPending: detailsPending,
    isFetching: detailsFetching,
  } = FetchTicketProcessingReviewDetails({
    id: activeTicket?.id,
    isSubmitted: activeTicket?.isSubmitted,
  });

  const {
    mutateAsync: submitProcessingTicket,
    isPending: ticketSubmitPending,
  } = SubmitTicketProcessingReview();

  const handleSubmitModalOpen = () => {
    setIsSubmitModalOpen(true);
  };

  const handleSubmitModalClose = () => {
    setIsSubmitModalOpen(false);
  };

  const handleReviewModalOpen = () => {
    setIsReviewModalOpen(true);
  };

  const handleReviewModalClose = () => {
    setIsReviewModalOpen(false);
  };

  const handleReplaceTktModalOpen = () => {
    setIsReplaceTktModalOpen(true);
  };

  const handleReplaceTktModalClose = () => {
    setIsReplaceTktModalOpen(false);
  };

  const handleSearch = (val?: string) => {
    const searchVal = val || "";
    setSearchInputValue(searchVal);
    handleQueriesInvalidate();
  };

  const handleSearchClick = (currentSearchValue: string) => {
    setSearchInputValue(currentSearchValue);
  };

  const handleQueriesInvalidate = () => {
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.FETCH_TICKET_PROCESSING_SECTION_COUNT_KEY],
    });
    refetch();
  };

  const handleRefetchOnReplaceTicket = () => {
    refetch();
    refetchDetails();
  };

  useEffect(() => {
    if (!activeTicket?.id) {
      setTicketData({});
    } else {
      setTicketData(ticketDetails);
    }
  }, [ticketDetails, activeTicket]);

  useEffect(() => {
    refetchDetails();
    const handleBeforeUnload = () => {
      sessionStorage.setItem(
        storageKeys.ACTIVE_TICKET_STATE,
        JSON.stringify(activeTicket),
      );
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [activeTicket]);

  useEffect(() => {
    if (skipRefreshRef?.current) {
      skipRefreshRef.current = false;
      return;
    }

    if (!searchInputValue && searchInputValue !== "" && !restoredFromSession) {
      if (window.location.hash.slice(1) === TabDataTypes.OPEN) {
        if (!!data?.pendingTickets?.length) {
          setActiveTicket(data?.pendingTickets?.[0]);
        } else if (!!data?.submittedTickets?.length) {
          setActiveTicket(data?.submittedTickets?.[0]);
        } else {
          setActiveTicket({});
        }
      } else if (window.location.hash.slice(1) === TabDataTypes.REVIEW) {
        if (!!data?.pendingTickets?.length) {
          setActiveTicket(data?.pendingTickets?.[0]);
        } else {
          setActiveTicket({});
        }
      }
    }
  }, [window.location.hash, data]);

  // Add this useEffect to update active ticket when data changes
  useEffect(() => {
    if (data && activeTicket?.id) {
      // Check if active ticket still exists in current data
      const isTicketInPending = data?.pendingTickets?.some(
        (ticket) => ticket.id === activeTicket.id,
      );
      const isTicketInSubmitted = data?.submittedTickets?.some(
        (ticket) => ticket.id === activeTicket.id,
      );

      // If active ticket no longer exists in current data, update to first available ticket
      if (!isTicketInPending && !isTicketInSubmitted) {
        if (window.location.hash.slice(1) === TabDataTypes.OPEN) {
          if (!!data?.pendingTickets?.length) {
            setActiveTicket(data?.pendingTickets?.[0]);
          } else if (!!data?.submittedTickets?.length) {
            setActiveTicket(data?.submittedTickets?.[0]);
          } else {
            setActiveTicket({});
          }
        } else if (window.location.hash.slice(1) === TabDataTypes.REVIEW) {
          if (!!data?.pendingTickets?.length) {
            setActiveTicket(data?.pendingTickets?.[0]);
          } else {
            setActiveTicket({});
          }
        }
      }
    }
  }, [data, activeTicket?.id, window.location.hash]);

  useEffect(() => {
    setSearchInputValue(undefined);
    refetch();
  }, []);

  useEffect(() => {
    const handleAfterLoad = () => {
      const storedValue = sessionStorage.getItem(
        storageKeys.ACTIVE_TICKET_STATE,
      );
      if (storedValue) {
        setRestoredFromSession(true);
        const activeTicketState = JSON.parse(storedValue);
        setActiveTicket(activeTicketState);
        sessionStorage.removeItem(storageKeys.ACTIVE_TICKET_STATE);
      }
    };
    handleAfterLoad();
  }, []);

  return (
    <>
      <TNLoader isLoading={ticketSubmitPending} />
      <div className={styles.dataInputContainer}>
        <div className={styles.mainSection}>
          <div ref={wrapperRef} className={styles.mainFileView}>
            <FileViewSection
              url={ticketData?.url ?? ticketDetails?.url ?? ""}
              wrapperRef={wrapperRef}
              isLoading={detailsFetching}
            />
          </div>
          <Form
            defaultValues={{
              dailySummary: false,
              signedByCustomer: ticketData?.signedByCustomer,
              ticketDate: null,
              ticketNumber: undefined,
              truckNumber: null,
              quantity: undefined,
              startTime: undefined,
              endTime: undefined,
              loadTime: undefined,
              loadCount: undefined,
            }}
            validationSchema={submitValidationSchema}
          >
            {({ getValues, control: { handleSubmit, _reset } }) => {
              const resetInputs = () => _reset();
              const handleSuccessNotification = () =>
                notification.success({
                  ...dataInputSuccess,
                  description: DataInputConstants.REVIEW_SUCCESS_MSG,
                });
              const handleSubmitTrigger = () => handleSubmitModalOpen();
              const handleValidationError = (errors) => {
                const keys = errors ? Object.keys(errors) : [];
                if (keys.includes(fieldConstants.SIGNED_BY_NAME)) {
                  message.error(DataInputConstants.ERROR_MSG_SIGNED_BY);
                }
              };
              const handleSubmitCall = async () => {
                const payloadData = {
                  id: activeTicket?.id,
                  ticketVerification: !isManagerReview
                    ? TicketSubmitAction.SUBMIT
                    : TicketSubmitAction.APPROVED,
                  dailySummary: !!getValues(fieldConstants.DAILY_SUMMARY_NAME),
                  ticketDate: moment(
                    getValues(fieldConstants.TICKET_DATE_NAME),
                  ).format(DateFormat.MM_DD_YYYY_SLASHED),
                  ticketNumber: getValues(fieldConstants.TICKET_NUMBER_NAME),
                  truckNumber: getValues(fieldConstants.TRUCK_NUMBER_NAME),
                  quantity: getValues(fieldConstants.QUANTITY_NAME),
                  startTime: getValues(fieldConstants.START_TIME_NAME),
                  endTime: getValues(fieldConstants.END_TIME_NAME),
                  loadCount: getValues(fieldConstants.LOAD_COUNT_NAME),
                  loadTime: getValues(fieldConstants.LOAD_TIME_NAME),
                  userId: userId,
                  reviewMetadata: ticketData?.reviewMetadata,
                };
                await submitProcessingTicket(payloadData, {
                  onSuccess: (res) => {
                    setTicketData(res.data);
                    handleSubmitModalClose();
                    if (
                      res.data.reviewMetadata &&
                      Object.keys(res.data.reviewMetadata).length > 0
                    ) {
                      notification.error({
                        description: res.message,
                      });
                      return;
                    }
                    notification.success({
                      ...dataInputSuccess,
                      description: res?.message,
                    });
                    resetInputs();
                    fieldsSectionRef.current?.resetFormFields();
                    const pendingList = data?.pendingTickets ?? [];
                    const currentIndex = pendingList.findIndex(
                      (t) => t.id === activeTicket?.id,
                    );
                    const nextTicket = !!pendingList[currentIndex + 1]
                      ? pendingList?.[currentIndex + 1]
                      : pendingList?.length > 1
                        ? pendingList?.[0]
                        : {};
                    if (nextTicket?.id) {
                      skipRefreshRef.current = true;
                      setActiveTicket(nextTicket);
                    }
                    handleQueriesInvalidate();
                  },
                  onError: (error) => {
                    handleSubmitModalClose();
                    message.error(
                      error?.response?.data?.message ??
                        DataInputConstants.RULE_ENGINE_ERROR,
                    );
                  },
                });
              };
              return (
                <>
                  <ReplaceTicketModal
                    isOpen={isReplaceTktModalOpen}
                    handleClose={handleReplaceTktModalClose}
                    activeTicketName={
                      ticketData?.ticketUploadDetails?.companyName
                    }
                    activeTicketUrl={ticketData?.url}
                    activeTicketId={activeTicket?.id}
                    handleFormReset={resetInputs}
                    handleInvalidateQueries={handleRefetchOnReplaceTicket}
                  />
                  <ManagerReviewModal
                    isOpen={isReviewModalOpen}
                    handleClose={handleReviewModalClose}
                    activeTicketId={activeTicket?.id}
                    handleInputReset={resetInputs}
                    successMsgTrigger={handleSuccessNotification}
                  />
                  <SubmitTicketModal
                    isOpen={isSubmitModalOpen}
                    handleClose={handleSubmitModalClose}
                    handleOnSubmit={handleSubmitCall}
                    isManager={isManagerReview}
                  />
                  <TicketProcessingInput
                    ref={fieldsSectionRef}
                    signedByCustomer={ticketData?.signedByCustomer}
                    errorKeys={ticketData?.errorKeyMap}
                    prefillData={ticketData?.prefillData}
                    reviewNotes={ticketData?.reviewReason}
                    reviewMetaData={ticketData?.reviewMetadata}
                    isManagerReview={isManagerReview}
                    activeTicketId={ticketData?.ticketId}
                    isSubmitted={activeTicket?.isSubmitted}
                    handleTicketSubmit={handleSubmit(
                      isManagerReview ? handleSubmitTrigger : handleSubmitCall,
                      handleValidationError,
                    )}
                    handleManagerReview={handleReviewModalOpen}
                    handleReplaceTicket={handleReplaceTktModalOpen}
                  />
                </>
              );
            }}
          </Form>
          <TicketProcessingDetails
            ticketData={ticketData}
            isLoading={detailsFetching}
          />
        </div>
        <div className={styles.sidePanel}>
          <div className={styles.sidePanelHeader}>
            {`${
              isManagerReview
                ? sideBarConstants.SIDE_PANEL_MANAGER_REVIEW_TITLE
                : sideBarConstants.SIDE_PANEL_PENDING_TITLE
            } (${data?.pendingTickets?.length ?? 0})`}
          </div>
          <Form>
            {({}) => (
              <TicketSearchInput
                handleSearch={handleSearch}
                setSearchState={handleSearchClick}
                searchInputValue={searchInputValue}
              />
            )}
          </Form>
          <div className={styles.pdfOptionList}>
            {data?.pendingTickets?.length > 0 ? (
              data?.pendingTickets?.map((item, index) => {
                return (
                  <Skeleton
                    title={false}
                    paragraph={{ rows: 2 }}
                    active
                    loading={isFetching}
                  >
                    <PDFOption
                      id={item?.id}
                      name={item?.companyName}
                      uploadDate={item?.uploadDate}
                      showAlert={item?.errorTicket}
                      isSubmitted={false}
                      isManagerReview={isManagerReview}
                      onClick={() => setActiveTicket(item)}
                      isActive={item?.id === activeTicket?.id}
                    />
                  </Skeleton>
                );
              })
            ) : (
              <ConditionalRender
                visible={!isPending}
                fallback={
                  <>
                    {dummyArray?.map((item) => (
                      <Skeleton
                        title={false}
                        paragraph={{ rows: 2 }}
                        active
                        loading={isFetching}
                      />
                    ))}
                  </>
                }
              >
                <div className={styles.emptySection}>
                  <p>{DataInputConstants.NO_TICKETS_FOUND}</p>
                </div>
              </ConditionalRender>
            )}
          </div>
          <ConditionalRender visible={!isManagerReview}>
            <div className={styles.sidePanelHeader}>
              {sideBarConstants.SIDE_PANEL_SUBMITTED_TITLE}
            </div>
          </ConditionalRender>
          <ConditionalRender visible={!isManagerReview}>
            <div className={styles.pdfOptionList}>
              {data?.submittedTickets?.length > 0 ? (
                data?.submittedTickets?.map((item, index) => (
                  <Skeleton
                    title={false}
                    paragraph={{ rows: 2 }}
                    active
                    loading={isFetching}
                  >
                    <PDFOption
                      id={item?.id}
                      name={item?.companyName}
                      uploadDate={item?.uploadDate}
                      isSubmitted={true}
                      showAlert={item?.submissionData?.errorKeys}
                      submissionData={item?.submissionData}
                      onClick={() => setActiveTicket(item)}
                      isActive={item?.id === activeTicket?.id}
                    />
                  </Skeleton>
                ))
              ) : (
                <ConditionalRender
                  visible={!isPending}
                  fallback={
                    <>
                      {dummyArray?.map((item) => (
                        <Skeleton
                          title={false}
                          paragraph={{ rows: 2 }}
                          active
                          loading={isFetching}
                        />
                      ))}
                    </>
                  }
                >
                  <div className={styles.emptySection}>
                    <p>{DataInputConstants.NO_TICKETS_FOUND}</p>
                  </div>
                </ConditionalRender>
              )}
            </div>
          </ConditionalRender>
        </div>
      </div>
    </>
  );
};

export default DataInput;
